﻿import { GeoJSON } from 'ol/format';
import { Feature } from 'ol';
import GeometryCollection from 'ol/geom/GeometryCollection.js';
import LineString from 'ol/geom/LineString.js';
import MultiLineString from 'ol/geom/MultiLineString.js';
import MultiPoint from 'ol/geom/MultiPoint.js';
import MultiPolygon from 'ol/geom/MultiPolygon.js';
import Point from 'ol/geom/Point.js';
import Polygon from 'ol/geom/Polygon.js';
import { transformGeometryWithOptions } from 'ol/format/Feature.js';
import { equivalent, get as getProjection } from 'ol/proj.js';

const ISO_DATE_TIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?(?:Z|[+\-]\d{2}:\d{2})$/;

const CANONICAL_PROJECTION_CODES = ['EPSG:4326', 'EPSG:3857'];

// Aliasurile din "crs" (ex. urn:ogc:def:crs:OGC:1.3:CRS84) nu primesc transformari proj4 la register(),
// iar OL 10.6+ nu mai face fallback pe identity; le normalizam la codul canonic echivalent.
function normalizeProjection(projection) {
    if (!projection) {
        return projection;
    }
    for (const code of CANONICAL_PROJECTION_CODES) {
        const canonical = getProjection(code);
        if (canonical && projection !== canonical && equivalent(projection, canonical)) {
            return canonical;
        }
    }
    return projection;
}
// Kept for potential future use; set to true to re-enable runtime date parsing.
const ENABLE_DATE_PARSE_AUGMENTATION = false;

class XGeoJSON extends GeoJSON {
    constructor(options) {
        super(options);
        this.layerId = options.layerId;
        this.layerSettings = options.layerSettings;
        this.selectionHandler = options.selectionHandler;
        this.generalActionHandler = options.generalActionHandler;
        this.parseDateProperties = ENABLE_DATE_PARSE_AUGMENTATION && options.parseDateProperties === true;
        this.useWorkerGeoJson = options.useWorkerGeoJson !== false;
        this.workerBatchSize = Number.isFinite(options.workerBatchSize) ? Math.max(100, Math.floor(options.workerBatchSize)) : 500;
        this._dateFieldState = Object.create(null);
        this._editFeatureCache = {
            layerId: null,
            idField: null,
            sourceRef: null,
            idSet: null
        };
    }

    _getEntityPkFieldName() {
        return this.layerSettings && this.layerSettings.Entity
            ? this.layerSettings.Entity.PkFieldName
            : null;
    }

    _getSelectionFeaturesMap() {
        if (!this.selectionHandler || !this.selectionHandler.selectionHolder || !this.selectionHandler.selectionHolder.layers) {
            return null;
        }

        const selectedLayer = this.selectionHandler.selectionHolder.layers[this.layerId];
        return selectedLayer && selectedLayer.features ? selectedLayer.features : null;
    }

    _getEditingFeatureIdSet(idField) {
        if (!idField || !this.generalActionHandler || !this.generalActionHandler.generalActionControls) {
            return null;
        }

        const editControl = this.generalActionHandler.generalActionControls.editControl;
        const interactionHandler = editControl && editControl.featureInteractionHandler
            ? editControl.featureInteractionHandler
            : null;

        if (!interactionHandler || interactionHandler.layerId !== this.layerId || !Array.isArray(interactionHandler.features)) {
            return null;
        }

        const sourceRef = interactionHandler.features;
        const cache = this._editFeatureCache;

        if (cache.sourceRef !== sourceRef || cache.layerId !== this.layerId || cache.idField !== idField) {
            const idSet = new Set();
            for (let i = 0; i < sourceRef.length; i++) {
                const editFeature = sourceRef[i];
                if (editFeature && typeof editFeature.get === 'function') {
                    idSet.add(editFeature.get(idField));
                }
            }

            cache.sourceRef = sourceRef;
            cache.layerId = this.layerId;
            cache.idField = idField;
            cache.idSet = idSet;
        }

        return cache.idSet;
    }

    _maybeAddDateTimestampProperties(properties) {
        if (!this.parseDateProperties || !properties) {
            return properties;
        }

        const keys = Object.keys(properties);
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            const value = properties[key];

            if (value == null || typeof value !== 'string' || value.length < 19 || value.indexOf('T') < 0) {
                continue;
            }

            let isDateField = this._dateFieldState[key];
            if (isDateField === undefined) {
                isDateField = ISO_DATE_TIME_REGEX.test(value);
                this._dateFieldState[key] = isDateField;
            }

            if (!isDateField) {
                continue;
            }

            const ts = Date.parse(value);
            if (Number.isFinite(ts)) {
                properties[key + '_tst'] = ts;
            }
        }

        return properties;
    }

    readProjection(object) {
        return normalizeProjection(super.readProjection(object));
    }

    readFeatureFromObject(object, options) {
        /**
         * @type {GeoJSONFeature}
         */
        let geoJSONFeature = null;
        if (object['type'] === 'Feature') {
            geoJSONFeature = /** @type {GeoJSONFeature} */ (object);
        } else {
            geoJSONFeature = {
                'type': 'Feature',
                'geometry': /** @type {GeoJSONGeometry} */ (object),
                'properties': null,
            };
        }

        const geometry = readGeometry(geoJSONFeature['geometry'], options);
            //.simplify(100);
        const feature = new Feature();
        if (this.geometryName_) {
            feature.setGeometryName(this.geometryName_);
        } else if (
            this.extractGeometryName_ &&
            'geometry_name' in geoJSONFeature !== undefined
        ) {
            feature.setGeometryName(geoJSONFeature['geometry_name']);
        }
        feature.setGeometry(geometry);

        if ('id' in geoJSONFeature) {
            feature.setId(geoJSONFeature['id']);
        }

        const properties = geoJSONFeature['properties'];
        if (properties) {
            this._maybeAddDateTimestampProperties(properties);
            feature.setProperties(properties, true);
        }

        if (this.layerId != null) {
            feature.layerId = this.layerId;
        }

        const idField = this._getEntityPkFieldName();
        const featureIdValue = idField != null ? feature.get(idField) : null;
        const selectedFeatures = this._getSelectionFeaturesMap();
        const editFeatureIds = this._getEditingFeatureIdSet(idField);

        if (selectedFeatures != null && featureIdValue != null && selectedFeatures[featureIdValue] != null) {
            feature.set('xstate', 2);
            selectedFeatures[featureIdValue] = feature;
        } else if (editFeatureIds != null && featureIdValue != null && editFeatureIds.has(featureIdValue)) {
            feature.set('xstate', 4);
        } else {
            feature.set('xstate', 0);
        }

        return feature;
    }
}

function readGeometry(object, options) {
    if (!object) {
        return null;
    }
    if (!object['type'])
    {
        return null;
    }
    /**
     * @type {import("../geom/Geometry.js").default}
     */
    let geometry;
    switch (object['type']) {
        case 'Point': {
            geometry = readPointGeometry(/** @type {GeoJSONPoint} */(object));
            break;
        }
        case 'LineString': {
            geometry = readLineStringGeometry(
        /** @type {GeoJSONLineString} */(object)
            );
            break;
        }
        case 'Polygon': {
            geometry = readPolygonGeometry(/** @type {GeoJSONPolygon} */(object));
            break;
        }
        case 'MultiPoint': {
            geometry = readMultiPointGeometry(
        /** @type {GeoJSONMultiPoint} */(object)
            );
            break;
        }
        case 'MultiLineString': {
            geometry = readMultiLineStringGeometry(
        /** @type {GeoJSONMultiLineString} */(object)
            );
            break;
        }
        case 'MultiPolygon': {
            geometry = readMultiPolygonGeometry(
        /** @type {GeoJSONMultiPolygon} */(object)
            );
            break;
        }
        case 'GeometryCollection': {
            geometry = readGeometryCollectionGeometry(
        /** @type {GeoJSONGeometryCollection} */(object)
            );
            break;
        }
        default: {
            throw new Error('Unsupported GeoJSON type: ' + object['type']);
        }
    }
    return transformGeometryWithOptions(geometry, false, options);
}

/**
 * @param {GeoJSONGeometryCollection} object Object.
 * @param {import("./Feature.js").ReadOptions} [options] Read options.
 * @return {GeometryCollection} Geometry collection.
 */
function readGeometryCollectionGeometry(object, options) {
    const geometries = object['geometries'].map(
        /**
         * @param {GeoJSONGeometry} geometry Geometry.
         * @return {import("../geom/Geometry.js").default} geometry Geometry.
         */
        function (geometry) {
            return readGeometry(geometry, options);
        }
    );
    return new GeometryCollection(geometries);
}

/**
 * @param {GeoJSONPoint} object Object.
 * @return {Point} Point.
 */
function readPointGeometry(object) {
    return new Point(object['coordinates']);
}

/**
 * @param {GeoJSONLineString} object Object.
 * @return {LineString} LineString.
 */
function readLineStringGeometry(object) {
    return new LineString(object['coordinates']);
}

/**
 * @param {GeoJSONMultiLineString} object Object.
 * @return {MultiLineString} MultiLineString.
 */
function readMultiLineStringGeometry(object) {
    return new MultiLineString(object['coordinates']);
}

/**
 * @param {GeoJSONMultiPoint} object Object.
 * @return {MultiPoint} MultiPoint.
 */
function readMultiPointGeometry(object) {
    return new MultiPoint(object['coordinates']);
}

/**
 * @param {GeoJSONMultiPolygon} object Object.
 * @return {MultiPolygon} MultiPolygon.
 */
function readMultiPolygonGeometry(object) {
    return new MultiPolygon(object['coordinates']);
}

/**
 * @param {GeoJSONPolygon} object Object.
 * @return {Polygon} Polygon.
 */
function readPolygonGeometry(object) {
    return new Polygon(object['coordinates']);
}

/**
 * @param {import("../geom/Geometry.js").default} geometry Geometry.
 * @param {import("./Feature.js").WriteOptions} [options] Write options.
 * @return {GeoJSONGeometry} GeoJSON geometry.
 */
function writeGeometry(geometry, options) {
    geometry = transformGeometryWithOptions(geometry, true, options);
    const type = geometry.getType();

    /** @type {GeoJSONGeometry} */
    let geoJSON;
    switch (type) {
        case 'Point': {
            geoJSON = writePointGeometry(/** @type {Point} */(geometry), options);
            break;
        }
        case 'LineString': {
            geoJSON = writeLineStringGeometry(
        /** @type {LineString} */(geometry),
                options
            );
            break;
        }
        case 'Polygon': {
            geoJSON = writePolygonGeometry(
        /** @type {Polygon} */(geometry),
                options
            );
            break;
        }
        case 'MultiPoint': {
            geoJSON = writeMultiPointGeometry(
        /** @type {MultiPoint} */(geometry),
                options
            );
            break;
        }
        case 'MultiLineString': {
            geoJSON = writeMultiLineStringGeometry(
        /** @type {MultiLineString} */(geometry),
                options
            );
            break;
        }
        case 'MultiPolygon': {
            geoJSON = writeMultiPolygonGeometry(
        /** @type {MultiPolygon} */(geometry),
                options
            );
            break;
        }
        case 'GeometryCollection': {
            geoJSON = writeGeometryCollectionGeometry(
        /** @type {GeometryCollection} */(geometry),
                options
            );
            break;
        }
        case 'Circle': {
            geoJSON = {
                type: 'GeometryCollection',
                geometries: [],
            };
            break;
        }
        default: {
            throw new Error('Unsupported geometry type: ' + type);
        }
    }
    return geoJSON;
}

/**
 * @param {GeometryCollection} geometry Geometry.
 * @param {import("./Feature.js").WriteOptions} [options] Write options.
 * @return {GeoJSONGeometryCollection} GeoJSON geometry collection.
 */
function writeGeometryCollectionGeometry(geometry, options) {
    options = Object.assign({}, options);
    delete options.featureProjection;
    const geometries = geometry.getGeometriesArray().map(function (geometry) {
        return writeGeometry(geometry, options);
    });
    return {
        type: 'GeometryCollection',
        geometries: geometries,
    };
}

/**
 * @param {LineString} geometry Geometry.
 * @param {import("./Feature.js").WriteOptions} [options] Write options.
 * @return {GeoJSONGeometry} GeoJSON geometry.
 */
function writeLineStringGeometry(geometry, options) {
    return {
        type: 'LineString',
        coordinates: geometry.getCoordinates(),
    };
}

/**
 * @param {MultiLineString} geometry Geometry.
 * @param {import("./Feature.js").WriteOptions} [options] Write options.
 * @return {GeoJSONGeometry} GeoJSON geometry.
 */
function writeMultiLineStringGeometry(geometry, options) {
    return {
        type: 'MultiLineString',
        coordinates: geometry.getCoordinates(),
    };
}

/**
 * @param {MultiPoint} geometry Geometry.
 * @param {import("./Feature.js").WriteOptions} [options] Write options.
 * @return {GeoJSONGeometry} GeoJSON geometry.
 */
function writeMultiPointGeometry(geometry, options) {
    return {
        type: 'MultiPoint',
        coordinates: geometry.getCoordinates(),
    };
}

/**
 * @param {MultiPolygon} geometry Geometry.
 * @param {import("./Feature.js").WriteOptions} [options] Write options.
 * @return {GeoJSONGeometry} GeoJSON geometry.
 */
function writeMultiPolygonGeometry(geometry, options) {
    let right;
    if (options) {
        right = options.rightHanded;
    }
    return {
        type: 'MultiPolygon',
        coordinates: geometry.getCoordinates(right),
    };
}

/**
 * @param {Point} geometry Geometry.
 * @param {import("./Feature.js").WriteOptions} [options] Write options.
 * @return {GeoJSONGeometry} GeoJSON geometry.
 */
function writePointGeometry(geometry, options) {
    return {
        type: 'Point',
        coordinates: geometry.getCoordinates(),
    };
}

/**
 * @param {Polygon} geometry Geometry.
 * @param {import("./Feature.js").WriteOptions} [options] Write options.
 * @return {GeoJSONGeometry} GeoJSON geometry.
 */
function writePolygonGeometry(geometry, options) {
    let right;
    if (options) {
        right = options.rightHanded;
    }
    return {
        type: 'Polygon',
        coordinates: geometry.getCoordinates(right),
    };
}

export default XGeoJSON;
